import Vue from 'vue'
import Vuex from 'vuex'
import { RENT_UPDATE_MODE_IDS } from '@/constants'

Vue.use(Vuex)

const MAX_RETRY_ATTEMPTS = 5
const POLLING_INTERVAL_MS = 15000

let visibilityListenersInitialized = false

const initialState = () => ({
  eventSource: null,
  pollingTimer: null,
  retryAttempts: 0,
  toyUpdates: {},
  isPageVisible: true,
  connectionLostWhileHidden: false,
  initialized: false,
  mode: RENT_UPDATE_MODE_IDS.SSE,
  lastUpdatedAt: null
})

const state = initialState()

const mutations = {
  FORCE_CLOSE_EVENT_SOURCE(state) {
    if (state.eventSource) {
      console.warn('Fechando SSE manualmente (force close).')
      state.eventSource.close()
      state.eventSource = null
    }
  },
  SET_INITIALIZED(state, value) {
    state.initialized = value
  },
  SET_MODE(state, mode) {
    state.mode = mode || RENT_UPDATE_MODE_IDS.SSE
  },
  SET_EVENT_SOURCE(state, eventSource) {
    state.eventSource = eventSource
  },
  CLOSE_EVENT_SOURCE(state) {
    if (state.eventSource) {
      state.eventSource.close()
      state.eventSource = null
    }
  },
  SET_POLLING_TIMER(state, timer) {
    state.pollingTimer = timer
  },
  CLEAR_POLLING_TIMER(state) {
    if (state.pollingTimer) {
      clearInterval(state.pollingTimer)
      state.pollingTimer = null
    }
  },
  SET_RETRY_ATTEMPTS(state, attempts) {
    state.retryAttempts = attempts
  },
  SET_PAGE_VISIBILITY(state, isVisible) {
    state.isPageVisible = isVisible
  },
  SET_CONNECTION_LOST_WHILE_HIDDEN(state, value) {
    state.connectionLostWhileHidden = value
  },
  SET_LAST_UPDATED_AT(state, value) {
    state.lastUpdatedAt = value
  },
  SET_TOY_UPDATE(state, payload) {
    Object.keys(payload).forEach((shoppingId) => {
      Vue.set(state?.toyUpdates, shoppingId, payload[shoppingId])
    })
  },
  RESET_STATE(state) {
    if (state.eventSource) state.eventSource.close()
    if (state.pollingTimer) clearInterval(state.pollingTimer)
    Object.assign(state, initialState())
  }
}

const getUserStoreIds = (rootGetters) => {
  const userStores = rootGetters['user/currentUserStores']
  if (!userStores || userStores.length === 0) return []
  return userStores.map((store) => store.id)
}

const getUserUpdateMode = (rootGetters) => {
  const mode = rootGetters['user/currentUserRentUpdateMode']
  return Object.values(RENT_UPDATE_MODE_IDS).includes(mode)
    ? mode
    : RENT_UPDATE_MODE_IDS.SSE
}

const actions = {
  initVisibilityListeners({ commit, dispatch }) {
    if (visibilityListenersInitialized) return
    visibilityListenersInitialized = true

    document.addEventListener('visibilitychange', () => {
      const isVisible = document.visibilityState === 'visible'
      commit('SET_PAGE_VISIBILITY', isVisible)
      if (isVisible) {
        dispatch('checkAndReconnectIfNeeded')
      }
    })

    window.addEventListener('focus', () => {
      commit('SET_PAGE_VISIBILITY', true)
      dispatch('checkAndReconnectIfNeeded')
    })

    window.addEventListener('blur', () => {
      commit('SET_PAGE_VISIBILITY', false)
    })
  },

  checkAndReconnectIfNeeded({ commit, dispatch, state }) {
    if (state.mode === RENT_UPDATE_MODE_IDS.POLLING) {
      if (!state.pollingTimer) {
        dispatch('startPolling')
      }
      return
    }

    if (state.connectionLostWhileHidden) {
      commit('SET_CONNECTION_LOST_WHILE_HIDDEN', false)
      commit('SET_RETRY_ATTEMPTS', 0)
      dispatch('startSSE')
    } else if (
      !state.eventSource ||
      (state.eventSource && state.eventSource.readyState !== EventSource.OPEN)
    ) {
      commit('SET_RETRY_ATTEMPTS', 0)
      dispatch('startSSE')
    }
  },

  startUpdates({ dispatch, state }) {
    if (state.mode === RENT_UPDATE_MODE_IDS.POLLING) {
      return dispatch('startPolling')
    }
    dispatch('fetchStoresSnapshot')
    return dispatch('startSSE')
  },

  setMode({ commit, dispatch, state }, mode) {
    const nextMode = Object.values(RENT_UPDATE_MODE_IDS).includes(mode)
      ? mode
      : RENT_UPDATE_MODE_IDS.SSE

    if (state.mode === nextMode) {
      dispatch('checkAndReconnectIfNeeded')
      dispatch('fetchStoresSnapshot')
      return
    }

    dispatch('stopUpdates')
    commit('SET_MODE', nextMode)
    dispatch('startUpdates')
  },

  startSSE({ commit, dispatch, rootGetters, state }) {
    const currentUserId = rootGetters['user/currentUserId']
    const storeIds = getUserStoreIds(rootGetters)

    if (!currentUserId || storeIds.length === 0) {
      console.error('ID do usuario ou lojas nao definidas.')
      return
    }

    commit('CLEAR_POLLING_TIMER')
    commit('CLOSE_EVENT_SOURCE')

    const apiUrl = process.env.VUE_APP_API_URL
    const url = `${apiUrl}/stores/stream/sse?userId=${encodeURIComponent(
      currentUserId
    )}&stores=${encodeURIComponent(JSON.stringify(storeIds))}`

    const eventSource = new EventSource(url)

    eventSource.onmessage = (event) => {
      try {
        const eventData = JSON.parse(event.data)
        commit('SET_TOY_UPDATE', eventData)
        commit('SET_LAST_UPDATED_AT', new Date())
        commit('SET_RETRY_ATTEMPTS', 0)
      } catch (err) {
        console.error('Erro ao parsear dados SSE:', err)
      }
    }

    eventSource.onopen = () => {
      if (state.retryAttempts > 0) {
        window.dispatchEvent(
          new CustomEvent('sse:reconnected', {
            detail: {
              at: new Date(),
              attempts: state.retryAttempts
            }
          })
        )
        commit('SET_RETRY_ATTEMPTS', 0)
      }
    }

    eventSource.onerror = (error) => {
      console.error('Erro na conexao SSE:', error)
      commit('CLOSE_EVENT_SOURCE')

      if (!state.isPageVisible) {
        commit('SET_CONNECTION_LOST_WHILE_HIDDEN', true)
        return
      }

      const currentAttempts = state.retryAttempts + 1
      commit('SET_RETRY_ATTEMPTS', currentAttempts)

      if (currentAttempts <= MAX_RETRY_ATTEMPTS) {
        setTimeout(() => {
          if (state.mode === RENT_UPDATE_MODE_IDS.SSE) {
            dispatch('startSSE')
          }
        }, 2000)
      } else {
        setTimeout(() => {
          window.location.reload()
        }, 2000)
      }
    }

    commit('SET_EVENT_SOURCE', eventSource)
  },

  async fetchStoresSnapshot({ commit, rootGetters, state }) {
    const storeIds = getUserStoreIds(rootGetters)
    if (storeIds.length === 0) return

    const apiUrl = process.env.VUE_APP_API_URL
    const url = `${apiUrl}/stores/stream/snapshot?stores=${encodeURIComponent(
      JSON.stringify(storeIds)
    )}`

    try {
      const response = await fetch(url, { cache: 'no-store' })
      if (!response.ok) {
        throw new Error('Erro ao buscar snapshot de alugueis.')
      }

      const payload = await response.json()
      commit('SET_TOY_UPDATE', payload)
      commit('SET_LAST_UPDATED_AT', new Date())
      commit('SET_RETRY_ATTEMPTS', 0)
    } catch (error) {
      console.error('Erro no polling de alugueis:', error)
      const currentAttempts = state.retryAttempts + 1
      commit('SET_RETRY_ATTEMPTS', currentAttempts)

      if (currentAttempts > MAX_RETRY_ATTEMPTS) {
        setTimeout(() => {
          window.location.reload()
        }, 2000)
      }
    }
  },

  startPolling({ commit, dispatch, rootGetters, state }) {
    const currentUserId = rootGetters['user/currentUserId']
    const storeIds = getUserStoreIds(rootGetters)

    if (!currentUserId || storeIds.length === 0) {
      console.error('ID do usuario ou lojas nao definidas.')
      return
    }

    commit('CLOSE_EVENT_SOURCE')
    commit('CLEAR_POLLING_TIMER')
    dispatch('fetchStoresSnapshot')

    const timer = setInterval(() => {
      if (state.mode === RENT_UPDATE_MODE_IDS.POLLING) {
        dispatch('fetchStoresSnapshot')
      }
    }, POLLING_INTERVAL_MS)

    commit('SET_POLLING_TIMER', timer)
  },

  stopUpdates({ commit }) {
    commit('CLOSE_EVENT_SOURCE')
    commit('CLEAR_POLLING_TIMER')
    commit('SET_RETRY_ATTEMPTS', 0)
    commit('SET_CONNECTION_LOST_WHILE_HIDDEN', false)
  },

  stopSSE({ dispatch }) {
    dispatch('stopUpdates')
  },

  init({ dispatch, commit, state, rootGetters }) {
    const mode = getUserUpdateMode(rootGetters)

    if (state.initialized) {
      commit('SET_MODE', mode)
      dispatch('initVisibilityListeners')
      dispatch('checkAndReconnectIfNeeded')
      dispatch('fetchStoresSnapshot')
      return
    }

    commit('SET_INITIALIZED', true)
    commit('SET_MODE', mode)
    dispatch('initVisibilityListeners')
    dispatch('startUpdates')
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
